/**
 * Admin Panel - Usage Analytics API
 *
 * Zod schemas for platform-wide usage monitoring endpoints.
 * Shows per-org leaderboard, channel adoption, usage source breakdown, model costs.
 */

import { z } from "zod";

import { ApiSuccessSchema } from "../../common/response.schema";

// ============================================================
// Common
// ============================================================

const UsageQuerySchema = z.object({
  days: z.coerce.number().min(1).max(365).optional().default(30)
});

// ============================================================
// Response Schemas
// ============================================================

const LeaderboardEntrySchema = z.object({
  organizationId: z.string(),
  organizationName: z.string(),
  costTenThousandths: z.number(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  usageCount: z.number()
});

const ChannelEntrySchema = z.object({
  channelType: z.string(),
  costTenThousandths: z.number(),
  usageCount: z.number(),
  orgCount: z.number()
});

const SourceEntrySchema = z.object({
  usageSource: z.string(),
  costTenThousandths: z.number(),
  usageCount: z.number()
});

const ModelEntrySchema = z.object({
  modelName: z.string(),
  costTenThousandths: z.number(),
  inputTokens: z.number(),
  outputTokens: z.number(),
  usageCount: z.number()
});

const PlatformTotalsSchema = z.object({
  totalCostTenThousandths: z.number(),
  totalInputTokens: z.number(),
  totalOutputTokens: z.number(),
  totalUsageRecords: z.number(),
  activeOrganizations: z.number(),
  costChangePercent: z.number(),
  days: z.number()
});

// ============================================================
// ZAdminUsage - Endpoint Definitions
// ============================================================

export const ZAdminUsage = {
  GetTotals: {
    method: "GET" as const,
    path: "/api/admin/usage/totals",
    Params: UsageQuerySchema,
    Response: ApiSuccessSchema(PlatformTotalsSchema)
  },

  GetLeaderboard: {
    method: "GET" as const,
    path: "/api/admin/usage/leaderboard",
    Params: UsageQuerySchema,
    Response: ApiSuccessSchema(
      z.object({
        leaderboard: z.array(LeaderboardEntrySchema),
        days: z.number()
      })
    )
  },

  GetChannels: {
    method: "GET" as const,
    path: "/api/admin/usage/channels",
    Params: UsageQuerySchema,
    Response: ApiSuccessSchema(
      z.object({
        channels: z.array(ChannelEntrySchema),
        days: z.number()
      })
    )
  },

  GetSources: {
    method: "GET" as const,
    path: "/api/admin/usage/sources",
    Params: UsageQuerySchema,
    Response: ApiSuccessSchema(
      z.object({
        sources: z.array(SourceEntrySchema),
        days: z.number()
      })
    )
  },

  GetModels: {
    method: "GET" as const,
    path: "/api/admin/usage/models",
    Params: UsageQuerySchema,
    Response: ApiSuccessSchema(
      z.object({
        models: z.array(ModelEntrySchema),
        days: z.number()
      })
    )
  }
} as const;
